import type { ActionsNetwork } from 'shared/store/actions'
import { fetchPageData } from 'shared/store/actions'
import endpoints from 'shared/store/endpoints'
import type { Dispatch } from 'redux'
import type { RootState } from 'shared/store/reducers'
import type { State } from './reducer'
import type {
    FilterShortDTO,
    MenuShortDTO,
    NutritionShortDTO,
    ProductShortDTO
} from 'shared/types/api'
import type { MenuElement, MenuSection } from './types'
import { MenuType } from './types'

type GetState = () => RootState

type ActionProductPageDataResponse = {
    type: 'PAGE_DATA_PRODUCT_RESPONSE',
    data: Object
}

type ActionProductTabSelected = {
    type: 'PAGE_DATA_PRODUCT_TAB_SELECTED',
    data: Object
}

type ActionProductElementSelected = {
    type: 'PAGE_DATA_PRODUCT_ELEMENT_SELECTED',
    data: Object
}

type ActionProductPageDataReset = {
    type: 'PAGE_DATA_PRODUCT_RESET'
}

type ActionProductError = {
    type: 'PAGE_DATA_PRODUCT_ERROR',
    error: boolean
}

const fetchProductPageData = (route: string, edito: boolean = false) =>
    fetchPageData(
        endpoints.public + route + `?edito=${edito ? 1 : 0}`,
        'PAGE_DATA_PRODUCT_RESPONSE'
    )

const handleElementClick = (element: MenuElement, section: MenuSection) => {
    return (dispatch: Dispatch<any>, getState: GetState) => {
        if (element && section) {
            const { menu, sections } = getState().productPageState

            let isSelected = false
            sections.map(sec => {
                if (section === sec) {
                    isSelected = sec.elements.map(el => {
                        return (el.selected = element === el)
                    })
                }
                return isSelected
            })

            let menuInformation = fillMenuInformation(menu, sections)

            dispatch({
                type: 'PAGE_DATA_PRODUCT_ELEMENT_SELECTED',
                data: {
                    sections: sections,
                    nutrition: menuInformation.nutrition,
                    noAllergens: menuInformation.noAllergens,
                    allergens: menuInformation.allergens,
                    traces: menuInformation.traces
                }
            })
        }
    }
}

const handleTabClick = (section: MenuSection) => {
    return (dispatch: Dispatch<any>, getState: GetState) => {
        if (section) {
            const { sections } = getState().productPageState

            const menu = sections.map(element => ({
                ...element,
                selected: element === section
            }))

            dispatch({
                type: 'PAGE_DATA_PRODUCT_TAB_SELECTED',
                data: {
                    sections: menu
                }
            })
        }
    }
}

const resetProductPageData = () => {
    return (dispatch: Dispatch<any>) => {
        dispatch({
            type: 'PAGE_DATA_PRODUCT_RESET'
        })
    }
}

const initState = (data: any, state: State) => {
    const { menu, product, type } = data

    let sections = []
    if (menu) {
        let mains = fillSection(menu.mains)
        if (mains.length > 0) {
            sections.push({
                elements: mains,
                type: MenuType.MAIN,
                selected: false
            })
        }

        let sides = fillSection(menu.sides)
        if (sides.length > 0) {
            sections.push({
                elements: sides,
                type: MenuType.SIDE,
                selected: false
            })
        }

        let drinks = fillSection(menu.drinks)
        if (drinks.length > 0) {
            sections.push({
                elements: drinks,
                type: MenuType.DRINK,
                selected: false
            })
        }

        let others = fillSection(menu.others)
        if (others.length > 0) {
            sections.push({
                elements: others,
                type: MenuType.OTHERS,
                selected: false
            })
        }

        for (let section of sections) {
            if (section.type === MenuType.MAIN) {
                if (type.toLowerCase() === 'kids') {
                    section.selected = true
                    break
                }
            } else {
                section.selected = true
                break
            }
        }

        let menuInformation = fillMenuInformation(menu, sections)

        return {
            ...state,
            ...data,
            image: menu.image,
            sections: sections,
            nutrition: menuInformation.nutrition,
            noAllergens: menuInformation.noAllergens,
            allergens: menuInformation.allergens,
            traces: menuInformation.traces,
            dataLoaded: true
        }
    } else if (product) {
        return {
            ...state,
            ...data,
            nutrition: product.nutrition,
            noAllergens: product.noAllergens,
            allergens: product.allergens,
            traces: product.traces,
            dataLoaded: true
        }
    } else {
        return state
    }
}

const fillMenuInformation = (
    menu: ?MenuShortDTO,
    sections: Array<MenuSection>
) => {
    /**
     * TODO: Need rework to not assign objects, but forEach instead of maps, etc...
     */
    let noAllergens = false
    let nutrition = []
    let allergens = []
    let traces = []

    if (menu) {
        if (menu.mains && menu.mains.length > 0) {
            const menuFirst = menu.mains[0]
            menuFirst.nutrition.map((n: NutritionShortDTO) => {
                return nutrition.push(Object.assign({}, n))
            })
            menuFirst.allergens.map((a: FilterShortDTO) => {
                return allergens.push(Object.assign({}, a))
            })
            menuFirst.traces.map((t: FilterShortDTO) => {
                return traces.push(Object.assign({}, t))
            })
            noAllergens = noAllergens && menuFirst.noAllergens
        }

        sections.map((section: MenuSection) => {
            if (section.type !== MenuType.MAIN) {
                return section.elements.map((mEl: MenuElement) => {
                    if (mEl.selected) {
                        mEl.nutrition.map((mEln: NutritionShortDTO, i) => {
                            return nutrition.map((n: NutritionShortDTO, j) => {
                                if (i === j) {
                                    // Initializing booleans to know if a symbol (">" or "<") is present in the contributed strings
                                    let symbols = {
                                        n: {
                                            share: {
                                                inferior: false,
                                                superior: false
                                            },
                                            portion: {
                                                inferior: false,
                                                superior: false
                                            }
                                        },
                                        mEln: {
                                            share: {
                                                inferior: false,
                                                superior: false
                                            },
                                            portion: {
                                                inferior: false,
                                                superior: false
                                            }
                                        }
                                    }
                                    // Looking if the strings contributed have symbols
                                    if (n.share && n.share.includes('<')) {
                                        symbols.n.share.inferior = true
                                    } else if (
                                        n.share &&
                                        n.share.includes('>')
                                    ) {
                                        symbols.n.share.superior = true
                                    }
                                    if (n.portion && n.portion.includes('<')) {
                                        symbols.n.portion.inferior = true
                                    } else if (
                                        n.portion &&
                                        n.portion.includes('>')
                                    ) {
                                        symbols.n.portion.superior = true
                                    }
                                    if (
                                        mEln.share &&
                                        mEln.share.includes('<')
                                    ) {
                                        symbols.mEln.share.inferior = true
                                    } else if (
                                        mEln.share &&
                                        mEln.share.includes('>')
                                    ) {
                                        symbols.mEln.share.superior = true
                                    }
                                    if (
                                        mEln.portion &&
                                        mEln.portion.includes('<')
                                    ) {
                                        symbols.mEln.portion.inferior = true
                                    } else if (
                                        mEln.portion &&
                                        mEln.portion.includes('>')
                                    ) {
                                        symbols.mEln.portion.superior = true
                                    }
                                    // If the string has a symbol, we only take the number value (">10" -> "10")
                                    // Deprecated : If the string has a symbol but its length is under 1, it means the number value is empty ("<") so we return "0"
                                    // Turns out the string can be a single number so we take it as is when len is 1
                                    const tempShare =
                                        n.share.length > 1
                                            ? symbols.n.share.superior
                                                ? n.share.split('>')[1]
                                                : symbols.n.share.inferior &&
                                                  n.share.split('<')[1]
                                            : n.share
                                    const tempPortion =
                                        n.portion.length > 1
                                            ? symbols.n.portion.superior
                                                ? n.portion.split('>')[1]
                                                : symbols.n.portion.inferior &&
                                                  n.portion.split('<')[1]
                                            : n.portion
                                    const tempMenuShare =
                                        mEln.share.length > 1
                                            ? symbols.mEln.share.inferior
                                                ? mEln.share.split('<')[1]
                                                : symbols.mEln.share.superior &&
                                                  mEln.share.split('>')[1]
                                            : mEln.share
                                    const tempMenuPortion =
                                        mEln.portion.length > 1
                                            ? symbols.mEln.portion.inferior
                                                ? mEln.portion.split('<')[1]
                                                : symbols.mEln.portion
                                                      .superior &&
                                                  mEln.portion.split('>')[1]
                                            : mEln.portion
                                    // We only get the number from the string, rounded to the next int if >= 10 and rounded to the tenth else
                                    // Rules are supposed to be more precise in theory
                                    let share =
                                        parseFloat(
                                            !!tempShare ? tempShare : n.share
                                        ) +
                                        parseFloat(
                                            !!tempMenuShare
                                                ? tempMenuShare
                                                : mEln.share
                                        )

                                    let portion =
                                        parseFloat(
                                            !!tempPortion
                                                ? tempPortion
                                                : n.portion
                                        ) +
                                        parseFloat(
                                            !!tempMenuPortion
                                                ? tempMenuPortion
                                                : mEln.portion
                                        )

                                    if (share >= 10) {
                                        n.share = Math.floor(share).toString()
                                    } else {
                                        n.share = (
                                            Math.round(share * 10) / 10
                                        ).toString()
                                    }

                                    return (n.portion =
                                        portion >= 10
                                            ? Math.floor(portion).toString()
                                            : (
                                                  Math.round(portion * 10) / 10
                                              ).toString())
                                }
                                return false
                            })
                        })
                        noAllergens = noAllergens && mEl.noAllergens
                        mEl.allergens.map((mEla: FilterShortDTO) => {
                            let exist = false
                            allergens.map((a: FilterShortDTO) => {
                                return mEla.code === a.code
                                    ? (exist = true)
                                    : false
                            })
                            if (!exist) {
                                allergens.push(mEla)
                            }
                            return false
                        })
                        return mEl.traces.map((mElt: FilterShortDTO) => {
                            let exist = false
                            traces.map((t: FilterShortDTO) => {
                                return mElt.code === t.code
                                    ? (exist = true)
                                    : false
                            })
                            if (!exist) {
                                traces.push(mElt)
                            }
                            return false
                        })
                    }
                    return false
                })
            }
            return false
        })
    }

    return {
        nutrition: nutrition,
        noAllergens: noAllergens,
        allergens: allergens,
        traces: traces
    }
}

const fillSection = (sectionArray: Array<ProductShortDTO>) => {
    let section = []
    if (sectionArray && sectionArray.length > 0) {
        sectionArray.map((element, index) => {
            const selected = index === 0
            section.push({
                ...element,
                selected: selected
            })
            return selected
        })
    }
    return section
}

const actionError = (error: boolean): ActionProductError => ({
    type: 'PAGE_DATA_PRODUCT_ERROR',
    error
})

export {
    initState,
    actionError,
    fetchProductPageData,
    resetProductPageData,
    handleElementClick,
    handleTabClick
}

export type Action =
    | ActionProductPageDataResponse
    | ActionProductTabSelected
    | ActionProductElementSelected
    | ActionProductPageDataReset
    | ActionProductError
    | ActionsNetwork
